import { useState } from 'react';
import { Card, CardContent, CardActions, Button, Box, Typography, Collapse, alpha } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StandardCard = ({ 
  icon, 
  title, 
  description, 
  link, 
  color, 
  buttonText,
  disabled = false,
  isWhatsApp = false,
  whatsAppNumber = '',
  whatsAppMessage = '',
  showDescriptionOnHover = false // New prop: hide description until hover/click
}) => {
  const theme = useTheme();
  const IconComponent = icon;
  const [hovered, setHovered] = useState(false);
  
  const colorMap = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main
  };
  
  const bgColor = alpha(colorMap[color] || theme.palette.primary.main, 0.15);
  const iconColor = colorMap[color] || theme.palette.primary.main;

  const handleWhatsAppClick = () => {
    if (isWhatsApp && !disabled) {
      const encodedMessage = encodeURIComponent(whatsAppMessage || 'Hello from Alpha01 Medical Consultants!');
      const whatsappUrl = `https://wa.me/${whatsAppNumber}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  return (
    <Card 
      onMouseEnter={() => showDescriptionOnHover && setHovered(true)}
      onMouseLeave={() => showDescriptionOnHover && setHovered(false)}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        animation: `${fadeIn} 0.5s ease-out`,
        borderRadius: '12px !important', // Override theme
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05) !important',
        overflow: 'hidden',
        border: '1px solid transparent',
        // Remove opacity from card - keep full contrast
        '&:hover': disabled ? {} : {
          transform: 'translateY(-4px) !important', // Override theme
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1) !important',
          borderColor: iconColor,
        }
      }}
    >
      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        textAlign: 'center',
        p: { xs: 2, md: 3 },
        minHeight: 140 // Ensure consistent content height
      }}>
        <Box sx={{ 
          bgcolor: bgColor, 
          color: iconColor,
          width: 64,
          height: 64,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': disabled ? {} : {
            transform: 'scale(1.05)',
          }
        }}>
          <IconComponent fontSize="medium" />
        </Box>
        <Typography variant="h6" component="h3" sx={{ 
          fontWeight: 600, 
          mb: description && !showDescriptionOnHover ? 1 : 0,
          fontSize: '1.1rem',
          lineHeight: 1.3
        }}>
          {title}
        </Typography>
        {description && (
          <Collapse in={!showDescriptionOnHover || hovered}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              fontSize: '0.85rem',
              lineHeight: 1.4,
              textAlign: 'center',
              mt: showDescriptionOnHover ? 0.5 : 0
            }}>
              {description}
            </Typography>
          </Collapse>
        )}
      </CardContent>
      <CardActions sx={{ p: 0, mt: 'auto' }}>
        <Button 
          size="medium" 
          fullWidth 
          component={disabled ? 'div' : (isWhatsApp ? 'button' : Link)} 
          to={disabled || isWhatsApp ? undefined : link}
          onClick={isWhatsApp ? handleWhatsAppClick : undefined}
          variant="contained"
          color={color}
          disabled={disabled}
            sx={{
              py: '12px !important', // Override theme padding
              px: '16px !important', // Override theme padding
              minHeight: '64px !important', // Increased to accommodate text wrapping
              height: '64px !important', // Fixed height for consistency
              borderRadius: '0 !important', // Square bottom for card
              boxShadow: 'none !important',
              fontSize: '0.875rem !important',
              fontWeight: '600 !important',
              textTransform: 'none !important',
              lineHeight: '1.2 !important', // Tighter line height for wrapped text
              transition: 'all 0.2s ease !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'center !important',
              textAlign: 'center !important',
              // Override disabled styling to maintain proper contrast
              '&.Mui-disabled': {
                backgroundColor: `${colorMap[color] || theme.palette.primary.main} !important`,
                color: 'white !important',
                opacity: '0.7 !important',
              },
              '&:hover': {
                boxShadow: 'none !important',
                transform: 'none !important', // Override theme transform
                opacity: disabled ? 0.7 : 0.9,
              },
              '&:active': {
                transform: 'none !important', // Override theme transform
              }
            }}
        >
          {buttonText || (disabled ? 'Coming Soon' : 'View All')}
        </Button>
      </CardActions>
    </Card>
  );
};

export default StandardCard;
